import React from 'react';
import { Tooltip } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Greys out (disables) its single child control for users who lack write access,
 * with an explanatory tooltip — the "graceful read-only" treatment for action
 * buttons (New / Edit / Delete / Save), instead of hiding them.
 *
 *   <WriteGuard module="labOrders"><Button onClick={...}>New Order</Button></WriteGuard>
 *   <WriteGuard capability="labResultAuth:write"><Button>Authorize</Button></WriteGuard>
 *
 * Admin/super-admin pass through (usePermissions grants them everything). The
 * child must accept a `disabled` prop (MUI Button/IconButton/etc. all do). The
 * server's authorize() remains the real gate; this is UX only.
 */
export function WriteGuard({ module, capability, children, tooltip = 'You have read-only access', disableInteractive = false }) {
    const { canWrite, can } = usePermissions();
    const allowed = capability ? can(capability) : module ? canWrite(module) : true;

    if (allowed) return children;

    const child = React.Children.only(children);
    const disabledChild = React.cloneElement(child, { disabled: true });

    // A disabled MUI control swallows pointer events, so wrap in a <span> for the
    // Tooltip to anchor to.
    return (
        <Tooltip title={tooltip} disableInteractive={disableInteractive}>
            <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{disabledChild}</span>
        </Tooltip>
    );
}

export default WriteGuard;
